/* global angular */

import extend from 'lodash/extend';

angular.module('atex.aceui.service').factory('AuthenticationService',
  ['$location', '$q', '$rootScope', '$window', 'Application', 'ApplicationConfigurationService',

    function ($location, $q, $rootScope, $window, Application, ApplicationConfigurationService) {
      var serviceHandler = undefined;

      var handler = function () {
        if (serviceHandler !== undefined) {
          return $q.when(serviceHandler);
        }

        return ApplicationConfigurationService.getServiceConfigurationFor('AuthenticationService').then(function (configuration) {
          var serviceUrl = atex.aceui.ServiceUtils.calculateServiceUrl(configuration, 'security/token');

          var serviceSettings = { customQ: atex.aceui.AngularUtils.applyingQ($q, $rootScope) };
          serviceHandler = atex.aceui.service.AuthenticationService(serviceUrl, serviceSettings);

          return serviceHandler;
        });
      };

      var execBuffer = [],
        authenticationInProgress = false,
        queue = function (execFn, deferred) {
          execBuffer.push([execFn, deferred]);
        };

      return {
      /**
       * Confirms the current authentication request (if any). Any requests
       * currently queued will be re-executed with the same parameters and
       * context that they had the first time. Calling this method
       * triggers the event aceui:authenticationSuccess.
       *
       * Does not return anything.
       */
        confirmAuthenticationRequest: function (user) {
          $rootScope.$broadcast('aceui:authenticationSuccess', user);

          var pendingRequests = execBuffer;
          execBuffer = [];

          for (var i = 0; i < pendingRequests.length; i++) {
            pendingRequests[i][0]();
          }

          authenticationInProgress = false;
        },

        /**
       * Cancels the current authentication request (if any). Any requests
       * currently queued will be rejected with an atex.aceui.error.Error object
       * where the 'type' property will be set to
       * atex.aceui.error.types.OPERATION_CANCELLED.
       *
       * Does not return anything.
       */
        cancelAuthenticationRequest: function () {
          var pendingRequests = execBuffer;
          execBuffer = [];

          for (var i = 0; i < pendingRequests.length; i++) {
            pendingRequests[i][1].reject(atex.aceui.error.Error({
              origin: 'AuthenticationService',
              type: atex.aceui.error.types.OPERATION_CANCELLED,
              cause: 'The operation was cancelled by the user!'
            }));
          }

          authenticationInProgress = false;
        },

        /**
       * Runs the given function wrapped by authentication logic. If the function
       * results in an error with the 'type' property set to
       * atex.aceui.error.types.AUTHENTICATION_FAILED, the request is queued and an
       * authentication request application event is broadcasted.
       *
       * Argument 'fn' - the function to run wrapped in authentication logic.
       *
       * Returns a Q-based promise.
       *
       * Resolves the returned promise with the output from the provided function
       * if the operation succeeds.
       *
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
        callWithAuthentication: function (fn) {
          var deferred = $q.defer(),
            that = this;

          var wrapped = function () {
            that.getCurrentUser().then(function (currentUser) {
              fn(currentUser).then(function (result) {
                deferred.resolve(result);
              }, function (error) {
                if (error.type === atex.aceui.error.types.AUTHENTICATION_FAILED) {
                  queue(wrapped, deferred);

                  if (!authenticationInProgress) {
                    authenticationInProgress = true;
                    $rootScope.$broadcast('aceui:authenticationRequest', currentUser);
                  }
                } else {
                  deferred.reject(error);
                }
              });
            });

            return deferred.promise;
          };

          return wrapped();
        },

        requireUser: function (currentUser) {
          var deferred = $q.defer();

          if (currentUser.userId !== undefined) {
            deferred.resolve();
          } else {
            queue(function () {
              deferred.resolve();
            }, deferred);

            if (!authenticationInProgress) {
              authenticationInProgress = true;
              $rootScope.$broadcast('aceui:authenticationRequest');
            }
          }

          return deferred.promise;
        },

        /**
       * Returns information on the current user. If there is no current user,
       * a login authentication event will be broadcasted throughout the
       * application. For more information on how the returned object looks, see
       * Application.currentUser.
       *
       * Returns a Q-based promise.
       *
       * Resolves the returned promise with information on the current user if the
       * operation succeeds.
       *
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
        getCurrentUser: function () {
          return this.requireUser(Application.currentUser).then(function () {
            return $q.when(Application.currentUser);
          });
        },

        /**
       * Verifies that the token stored for the current user is still valid.
       *
       * Returns a Q-based promise.
       *
       * Resolves the returned promise with the same output as you would get from
       * AuthenticationService.login if the operation succeeds.
       *
       * Rejects the return promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
        verifyLoggedIn: function () {
          return handler().then(function (serviceHandler) {
            return serviceHandler.validate(Application.currentUser.token).then(function (tokenData) {
              return tokenData;
            });
          });
        },

        /**
       * Verifies that the provided token is valid. And log in to the application.
       *
       * Returns a Q-based promise.
       *
       * Resolves the returned promise with the same output as you would get from
       * AuthenticationService.login if the operation succeeds.
       *
       * Rejects the return promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
        loginByToken: function (username, token) {
          return handler().then(function (serviceHandler) {
            return serviceHandler.validate(token).then(function (tokenData) {
              Application.currentUser = extend({ username: username }, tokenData);
              Application.setCookies();

              return tokenData.userId;
            });
          });
        },

        /**
       * Get a token limited for access to a particular resource
       *
       * Returns a Promise
       *
       * Resolves the returned promise with a token to be used for accessing the resource
       *
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
        getResourceToken: function (resource) {
          return this.getCurrentUser().then(function (currentUser) {
            return handler().then(function (serviceHandler) {
              return serviceHandler.getResourceToken(currentUser.username, currentUser.token, resource);
            });
          });
        },

        /**
       * Authenticates against the server using the given username and password.
       * Also sets the authenticated user as the current user in the application.
       *
       * Argument 'exchange' - An external token that can be exchanged to an Ace token
       * Argument 'username' - The username to authenticate with.
       * Argument 'password' - The password to authenticate with.
       *
       * Returns a promise.
       *
       * Resolves the returned promise with the user ID of the authenticated user
       * if the operation succeeds.
       *
       * Rejects the returned promise with an atex.aceui.error.Error object if the
       * operation fails.
       */
        login: function ({ username, password, exchange }) {
          return handler().then(function (serviceHandler) {
            return serviceHandler.authenticate ({ username, password, exchange })
              .then (function (authResponse) {

                if (exchange && username) {
                  if (username !== authResponse.userId) {
                    return Promise.reject(atex.aceui.error.Error ({
                      rootCause: {
                        message: 'Wrong user!<br>Please sign in as ' + username + ', or sign out to cancel.'
                      },
                      type: atex.aceui.error.OPERATION_CANCELLED,
                      origin: 'AuthenticationService'
                    }));
                  }
                }

                Application.currentUser = extend({ username: authResponse.userId, principalId: authResponse.userId }, authResponse);
                Application.setCookies();

                return authResponse;
              });
          });
        },

        logout: function () {
          $rootScope.$broadcast ('aceui:userLoggedOut');
          Application.reset();
          $location.path('/');
          $window.location.reload();
        }
      };
    }
  ]);
